import React, { useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import { motion } from 'framer-motion';

export default function ResumeManager({ resumeUrl, resumeName, onUpdate }) {
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            setError('File size must be less than 5MB');
            return;
        }

        if (file.type !== 'application/pdf') {
            setError('Only PDF files are allowed');
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const fileExt = file.name.split('.').pop();
            const fileName = `${user.id}/${Date.now()}.${fileExt}`;

            // Upload file
            const { error: uploadError } = await supabase.storage
                .from('resumes')
                .upload(fileName, file, {
                    cacheControl: '3600',
                    upsert: false
                });

            if (uploadError) throw uploadError;

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('resumes')
                .getPublicUrl(fileName);

            // Update profile
            const { error: updateError } = await supabase
                .from('profiles')
                .update({
                    resume_url: publicUrl,
                    resume_name: file.name
                })
                .eq('id', user.id);

            if (updateError) throw updateError;

            onUpdate(publicUrl, file.name);
        } catch (err) {
            console.error('Error uploading resume:', err);
            setError(err.message || 'Failed to upload resume');
        } finally {
            setIsUploading(false);
            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 shadow-sm border border-neutral-200 dark:border-neutral-800">
            <h3 className="text-lg font-semibold mb-4 text-neutral-900 dark:text-white flex items-center gap-2">
                <Icon name="FileText" size={20} />
                My Resume
            </h3>

            <div className="space-y-4">
                {resumeUrl ? (
                    <div className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-blue-600 dark:text-blue-400">
                                <Icon name="FileText" size={24} />
                            </div>
                            <div className="overflow-hidden">
                                <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                                    {resumeName || 'Uploaded Resume'}
                                </p>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400">PDF Document</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <a
                                href={resumeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-neutral-100 rounded-full transition-colors"
                                title="View"
                            >
                                <Icon name="Eye" size={18} />
                            </a>
                            <button
                                onClick={triggerFileInput}
                                className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-neutral-100 rounded-full transition-colors"
                                title="Replace"
                            >
                                <Icon name="Upload" size={18} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg">
                        <div className="mb-3 text-neutral-400">
                            <Icon name="UploadCloud" size={48} className="mx-auto" />
                        </div>
                        <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                            Upload your resume to easily apply for jobs
                        </p>
                        <Button onClick={triggerFileInput} disabled={isUploading}>
                            {isUploading ? 'Uploading...' : 'Upload PDF Resume'}
                        </Button>
                    </div>
                )}

                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".pdf"
                    className="hidden"
                />

                {error && (
                    <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm mt-2 text-center"
                    >
                        {error}
                    </motion.p>
                )}
            </div>
        </div>
    );
}
